/**
 * Dashboard Service
 * Service for fetching dashboard metrics and triage statistics
 */

import { acquireTokenSilent } from '../config/msalConfig';

export interface TriageMetrics {
  category_distribution: {
    category_1: number;
    category_2: number;
    category_3: number;
    category_4: number;
  };
  total_referrals: number;
  edited_vs_unedited: {
    edited_count: number;
    unedited_count: number;
    edited_percentage: number;
    unedited_percentage: number;
  };
  escalation_count: number;
  date_range: {
    from: string;
    to: string;
  };
}

export class DashboardService {
  private static baseUrl = (import.meta.env.VITE_FUNCTION_APP_URL || 'https://func-axis-referral-dev.azurewebsites.net') + '/api';

  /**
   * Get Entra ID token from MSAL
   */
  private static async getToken(): Promise<string> {
    try {
      const tokenResult = await acquireTokenSilent();
      return tokenResult.accessToken;
    } catch (error) {
      console.error('Failed to acquire token:', error);
      return '';
    }
  }

  /**
   * Get triage metrics for dashboard
   * @param dateFrom - Optional start date (ISO string)
   * @param dateTo - Optional end date (ISO string)
   * @returns Triage metrics including category distribution and edited vs unedited ratios
   */
  static async getTriageMetrics(dateFrom?: string, dateTo?: string): Promise<TriageMetrics> {
    try {
      const params = new URLSearchParams();
      if (dateFrom) {
        params.append('date_from', dateFrom);
      }
      if (dateTo) {
        params.append('date_to', dateTo);
      }

      const url = `${this.baseUrl}/dashboard/triage-metrics${params.toString() ? `?${params.toString()}` : ''}`;

      const token = await this.getToken();
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let message = `HTTP ${response.status}: Failed to fetch dashboard metrics`;
        try {
          const error = await response.json();
          message = error.message || error.error || message;
        } catch (e) {
          // Response body is not JSON, use generic message
        }
        throw new Error(message);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  }
}

